import {
  BAND_COUNT,
  DREAD_REASONS,
  MAX_REMINDERS,
  REMINDER_PRESETS,
  REPEAT_MAX_DAYS,
  completeOnce,
  firstDue,
  isResting,
  lastTouch,
  uncompleteOnce,
  wakeAt,
  type Band,
  type Card,
  type Conflict,
  type DreadReason,
  type Energy,
  type Motivation,
  type Reminder,
  type Repeat,
  type RepeatKind,
} from '@hmawiw/shared';
import { useEffect, useRef, useState } from 'react';
import { useOrderedBoards } from '../../hooks/useBoards';
import { useNow } from '../../hooks/useMedia';
import { useT } from '../../i18n';
import { formatShortDate, fromISODate, fromLocalDateTime, hasDueTime, toISODate, toTimeString, withTime } from '../../lib/dates';
import { useHour12 } from '../../lib/clock';
import { dueForHorizon, horizonBand } from '../../lib/horizon';
import { formatDateTime, formatRelative } from '../../lib/device';
import { haptic, isNative } from '../../native/platform';
import { timeLeft, useTimer } from '../../store/timer';
import { useCards } from '../../store/cards';
import { useEvents } from '../../store/events';
import { useSession } from '../../store/session';
import { useSettings } from '../../store/settings';
import { useUi } from '../../store/ui';
import controls from '../controls.module.css';
import { Alarm, Bell, Check, ChevronDown, ChevronLeft, ChevronRight, Cross, EnergyGlyph, Timer } from '../icons';
import { Sheet } from '../Sheet/Sheet';
import styles from './CardSheet.module.css';

const BANDS = Array.from({ length: BAND_COUNT }, (_, i) => i as Band);
const LEVELS: Energy[] = [1, 2, 3];
const REPEAT_KINDS: RepeatKind[] = ['after', 'weekly', 'monthly', 'yearly', 'ongoing'];
const LEADS = [0, 1, 2, 3, 7, 14, 30] as const;
const MONTHS = Array.from({ length: 12 }, (_, i) => i + 1);

/** Month names in the user's locale. */
function monthNames(locale: string): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { month: 'long' });
  return MONTHS.map((m) => fmt.format(new Date(2024, m - 1, 1)));
}
const WEEKDAYS = [1, 2, 3, 4, 5, 6, 7] as const;

function isoWeekday(ts: number): number {
  const w = new Date(ts).getDay();
  return w === 0 ? 7 : w;
}

/** Short weekday names in the user's locale, Monday first; 2024-01-01 was a Monday. */
function weekdayNames(locale: string): string[] {
  const fmt = new Intl.DateTimeFormat(locale, { weekday: 'short' });
  return WEEKDAYS.map((d) => fmt.format(new Date(2024, 0, d)));
}

/** The fields Cancel puts back; position fields go through moveCard so ranks stay sane. */
type Snapshot = Pick<Card, 'title' | 'notes' | 'firstStep' | 'motivation' | 'band' | 'energy' | 'dueAt' | 'reminders' | 'repeat'>;

function snapshotOf(card: Card): Snapshot {
  const { title, notes, firstStep, motivation, band, energy, dueAt, reminders, repeat } = card;
  return { title, notes, firstStep, motivation, band, energy, dueAt, reminders, repeat };
}

type SectionId = 'step' | 'plan' | 'dread';
const SECTIONS_KEY = 'hmawiw.sheet.sections';

function readSections(): Partial<Record<SectionId, boolean>> {
  try {
    const v = JSON.parse(localStorage.getItem(SECTIONS_KEY) ?? '{}') as Partial<Record<SectionId, boolean>>;
    return typeof v === 'object' && v !== null ? v : {};
  } catch {
    return {};
  }
}

function writeSections(v: Partial<Record<SectionId, boolean>>): void {
  try {
    localStorage.setItem(SECTIONS_KEY, JSON.stringify(v));
  } catch {
    // no storage
  }
}

/** A folded block of the sheet: one line with its name and what it holds, its fields under it when open. */
function Section({ id, title, summary, open, onToggle, children }: { id: SectionId; title: string; summary: string | null; open: boolean; onToggle(): void; children: React.ReactNode }) {
  return (
    <div className={styles.section} data-section={id}>
      <button type="button" className={styles.sectionHead} aria-expanded={open} onClick={onToggle}>
        <span className={styles.sectionTitle}>{title}</span>
        {summary && !open ? <span className={styles.sectionSummary}>{summary}</span> : null}
        <span className={`${styles.sectionChevron}${open ? ` ${styles.sectionOpen}` : ''}`}>
          <ChevronDown size={18} />
        </span>
      </button>
      {open ? <div className={styles.sectionBody}>{children}</div> : null}
    </div>
  );
}

export function CardSheet() {
  const t = useT();
  const id = useUi((s) => s.sheetCardId);
  const close = useUi((s) => s.closeSheet);
  const card = useCards((s) => (id ? s.cards[id] : undefined));
  const open = !!card && card.deletedAt === null && card.status === 'open';
  const [cancel, setCancel] = useState<(() => void) | null>(null);
  return (
    <Sheet open={open} label={t.t('card.open')} onClose={close} onCancel={cancel ?? undefined} className={styles.compact}>
      {card ? <SheetBody key={card.id} card={card} registerCancel={setCancel} /> : null}
    </Sheet>
  );
}

function SheetBody({ card, registerCancel }: { card: Card; registerCancel(fn: (() => void) | null): void }) {
  const t = useT();
  const bandPreset = useSettings((s) => s.bandPreset);
  const columnCount = useSettings((s) => s.columnCount);
  const now = useNow();
  const close = useUi((s) => s.closeSheet);
  const showToast = useUi((s) => s.showToast);
  const updateCard = useCards((s) => s.updateCard);
  const moveCard = useCards((s) => s.moveCard);
  const markDone = useCards((s) => s.markDone);
  const wake = useCards((s) => s.wake);
  const moveToBoard = useCards((s) => s.moveToBoard);
  const boards = useOrderedBoards();
  const logEvent = useEvents((s) => s.log);
  const restore = useCards((s) => s.restore);
  const remove = useCards((s) => s.remove);
  const undelete = useCards((s) => s.undelete);
  const dismissConflict = useCards((s) => s.dismissConflict);
  const conflicts = useCards((s) => s.conflicts);
  const mine = Object.values(conflicts).filter((c) => c.cardId === card.id);
  const [title, setTitle] = useState(card.title);
  const [notes, setNotes] = useState(card.notes ?? '');
  const [firstStep, setFirstStep] = useState(card.firstStep ?? '');
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [snapshot] = useState(() => snapshotOf(card));
  const notesEl = useRef<HTMLTextAreaElement>(null);
  const firstStepEl = useRef<HTMLInputElement>(null);
  const reminderEl = useRef<HTMLSelectElement>(null);
  // Folded sections: open on their own when they hold something; a tap overrides and is remembered.
  const [sections, setSections] = useState<Partial<Record<SectionId, boolean>>>(readSections);
  const toggleSection = (id: SectionId) => {
    const next = { ...sections, [id]: !sectionOpen(id) };
    setSections(next);
    writeSections(next);
  };
  const stepSummary = card.firstStep ?? (card.attempts.length ? t.tn('timer.tried', card.attempts.length) : null);

  // The notes field grows with its content instead of scrolling inside itself.
  useEffect(() => {
    const el = notesEl.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${el.scrollHeight}px`;
  }, [notes]);

  const commitTitle = () => {
    const next = title.trim();
    if (next && next !== card.title) void updateCard(card.id, { title: next });
    else if (!next) setTitle(card.title);
  };
  const commitNotes = () => {
    const next = notes.trim() || null;
    if (next !== card.notes) void updateCard(card.id, { notes: next });
  };
  const commitFirstStep = () => {
    const next = firstStep.trim() || null;
    if (next !== card.firstStep) void updateCard(card.id, { firstStep: next });
  };
  const nudge = (dir: 1 | -1) => {
    const to = card.motivation + dir;
    if (to < 0 || to >= columnCount) return;
    void moveCard(card.id, to as Motivation, card.band, 0, { cause: 'sheet' });
  };

  // The ten-minute attempt: start closes the sheet so the board shows the countdown; the
  // question after it moves the card along the motivation axis, which is the whole point.
  const running = useTimer((s) => s.running);
  const askFor = useTimer((s) => s.askFor);
  const startTimer = useTimer((s) => s.start);
  const stopTimer = useTimer((s) => s.stop);
  const answerTimer = useTimer((s) => s.answer);
  const tickNow = useNow(running?.cardId === card.id ? 1000 : 60_000);
  const runningHere = running?.cardId === card.id ? running : null;
  const feel = (dir: -1 | 0 | 1) => {
    answerTimer();
    void logEvent('feel', card.id, { answer: dir });
    if (dir === 0) return;
    const from = card.motivation;
    const to = from + dir;
    if (to < 0 || to >= columnCount) return;
    void moveCard(card.id, to as Motivation, card.band, 0, { cause: 'feel' });
    showToast(t.t('toast.movedTo', { column: t.column(to) }), {
      label: t.t('toast.undo'),
      run: () => {
        void moveCard(card.id, from, card.band, 0, { cause: 'undo' });
      },
    });
  };
  const lastAttempt = card.attempts.length ? card.attempts[card.attempts.length - 1] : null;
  const autoOpen: Record<SectionId, boolean> = {
    step: card.firstStep !== null || runningHere !== null || askFor === card.id,
    plan: card.dueAt !== null || card.repeat !== null || card.reminders.length > 0,
    dread: card.dread !== null,
  };
  const sectionOpen = (id: SectionId): boolean => sections[id] ?? autoOpen[id];
  const openSection = (id: SectionId) => {
    if (!sectionOpen(id)) {
      const next = { ...sections, [id]: true };
      setSections(next);
      writeSections(next);
    }
  };
  const planSummary = [
    card.dueAt !== null ? formatShortDate(card.dueAt, t.locale) : null,
    card.repeat ? t.t(`repeat.${card.repeat.kind}`) : null,
    card.reminders.length ? t.t('card.reminders', { n: card.reminders.length }) : null,
  ]
    .filter(Boolean)
    .join(' · ') || null;

  /** Everything back to what it was when the sheet opened, as ordinary edits so other devices follow. */
  const cancel = () => {
    const current = useCards.getState().cards[card.id];
    if (current) {
      const changes: Partial<Card> = {};
      for (const f of ['title', 'notes', 'firstStep', 'energy', 'dueAt'] as const) {
        if (current[f] !== snapshot[f]) (changes as Record<string, unknown>)[f] = snapshot[f];
      }
      if (JSON.stringify(current.reminders) !== JSON.stringify(snapshot.reminders)) changes.reminders = snapshot.reminders;
      if (JSON.stringify(current.repeat) !== JSON.stringify(snapshot.repeat)) changes.repeat = snapshot.repeat;
      if (Object.keys(changes).length) void updateCard(card.id, changes);
      if (current.motivation !== snapshot.motivation || current.band !== snapshot.band) void moveCard(card.id, snapshot.motivation, snapshot.band, 0, { cause: 'cancel' });
    }
    close();
  };
  useEffect(() => {
    registerCancel(() => cancel);
    return () => registerCancel(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const [doneAnim, setDoneAnim] = useState(false);
  const doneLabel = t.t(card.repeat?.kind === 'ongoing' ? 'sheet.touch' : card.repeat ? 'sheet.markDoneNext' : 'sheet.markDone');
  // The check fills and draws itself before the card goes: the one moment the app should feel good.
  const doneWithFlourish = () => {
    if (doneAnim) return;
    setDoneAnim(true);
    haptic('confirm');
    window.setTimeout(done, 380);
  };
  const done = () => {
    if (card.repeat) {
      // A recurring card is never done for good: the occurrence is logged and the date moves on.
      const before = uncompleteOnce(card);
      const changes = completeOnce(card, Date.now());
      void updateCard(card.id, changes);
      close();
      const text = card.repeat.kind === 'ongoing' ? t.t('toast.touched', { title: card.title }) : t.t('toast.next', { date: formatShortDate(changes.dueAt ?? Date.now(), t.locale) });
      showToast(text, {
        label: t.t('toast.undo'),
        run: () => {
          void updateCard(card.id, before);
        },
      });
      return;
    }
    void markDone(card.id);
    close();
    const undo = { label: t.t('toast.undo'), run: () => void restore(card.id) };
    if (card.motivation === 0) {
      // The forecast lever: one tap, skippable, on the cards that were dreaded.
      const answer = (value: 'easier' | 'worse') => () => void logEvent('forecast', card.id, { answer: value });
      showToast(t.t('toast.doneForecast', { title: card.title }), [{ label: t.t('forecast.easier'), run: answer('easier') }, { label: t.t('forecast.worse'), run: answer('worse') }, undo], 8000);
      return;
    }
    showToast(t.t('toast.done', { title: card.title }), undo);
  };

  const setRepeat = (repeat: Repeat | null) => {
    const changes: Partial<Card> = { repeat };
    // A dated rule needs a date to count from; today whenever today fits, so the card stays in view.
    // A calendar rule carries its date: when its month, day or weekdays change, the due date follows.
    const calendarOf = (r: Repeat | null) => (r && (r.kind === 'weekly' || r.kind === 'monthly' || r.kind === 'yearly') ? JSON.stringify({ ...r, lead: 0 }) : null);
    const calendarChanged = calendarOf(repeat) !== null && calendarOf(repeat) !== calendarOf(card.repeat);
    if (repeat && repeat.kind !== 'ongoing' && (card.dueAt === null || calendarChanged)) {
      const day = firstDue(repeat, now);
      changes.dueAt = card.dueAt !== null && hasDueTime(card.dueAt) ? withTime(day, toTimeString(card.dueAt)) : day;
    }
    void updateCard(card.id, changes);
  };
  const pickRepeatKind = (kind: string) => {
    if (kind === '') return setRepeat(null);
    const lead = card.repeat && card.repeat.kind !== 'ongoing' ? card.repeat.lead : 1;
    if (kind === 'after') return setRepeat({ kind, days: 7, lead });
    if (kind === 'weekly') return setRepeat({ kind, days: [card.dueAt !== null ? isoWeekday(card.dueAt) : isoWeekday(now)], lead });
    if (kind === 'monthly') return setRepeat({ kind, day: new Date(card.dueAt ?? now).getDate(), lead });
    if (kind === 'yearly') {
      const d = new Date(card.dueAt ?? now);
      return setRepeat({ kind, month: d.getMonth() + 1, day: d.getDate(), lead });
    }
    if (kind === 'ongoing') return setRepeat({ kind, days: 3 });
  };
  const clampDays = (raw: string, fallback: number): number => {
    const n = Number.parseInt(raw, 10);
    return Number.isFinite(n) ? Math.min(REPEAT_MAX_DAYS, Math.max(1, n)) : fallback;
  };
  const resting = isResting(card, now);
  const wakeDate = wakeAt(card);
  const del = () => {
    void remove(card.id);
    close();
    showToast(t.t('toast.deleted'), {
      label: t.t('toast.undo'),
      run: () => {
        void undelete(card.id);
      },
    });
  };

  const dueTimed = card.dueAt !== null && hasDueTime(card.dueAt);
  const hour12 = useHour12();
  const [customReminder, setCustomReminder] = useState<string | null>(null);
  const setReminders = (reminders: Reminder[]) => void updateCard(card.id, { reminders });
  const addReminder = (r: Reminder) => {
    if (card.reminders.length >= MAX_REMINDERS) return;
    if (card.reminders.some((x) => x.kind === r.kind && (x.kind === 'at' ? x.at === (r as { at: number }).at : x.ms === (r as { ms: number }).ms))) return;
    setReminders([...card.reminders, r]);
  };
  const onPickReminder = (value: string) => {
    if (value === 'custom') {
      setCustomReminder('');
      return;
    }
    const preset = REMINDER_PRESETS.find((x) => x.key === value);
    if (preset && card.dueAt !== null) addReminder({ kind: 'before', ms: preset.ms });
  };
  const reminderLabel = (r: Reminder): string => {
    if (r.kind === 'at') return formatDateTime(r.at, t.locale, hour12);
    const preset = REMINDER_PRESETS.find((x) => x.ms === r.ms);
    const when = preset ? t.t(preset.key === 'due' ? 'reminder.atDue' : (`reminder.before.${preset.key}` as 'reminder.before.1h')) : t.t('reminder.before.other', { hours: Math.round(r.ms / 3_600_000) });
    return card.dueAt === null ? `${when} · ${t.t('reminder.noDue')}` : `${when} · ${formatDateTime(card.dueAt - r.ms, t.locale, hour12)}`;
  };

  return (
    <>
      <div className={controls.row}>
        <input className={controls.field} value={title} onChange={(e) => setTitle(e.target.value)} onBlur={commitTitle} aria-label={t.t('sheet.title')} />
        <button
          type="button"
          className={`${styles.doneBtn}${doneAnim ? ` ${styles.doneGo}` : ''}`}
          onClick={doneWithFlourish}
          aria-label={doneLabel}
          title={doneLabel}
          data-done=""
        >
          <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path className={styles.doneStroke} d="M5 12.5l4.5 4.5L19 7" />
          </svg>
        </button>
      </div>
      <textarea ref={notesEl} rows={2} className={`${controls.field} ${styles.notes}`} value={notes} placeholder={t.t('sheet.notes')} onChange={(e) => setNotes(e.target.value)} onBlur={commitNotes} aria-label={t.t('sheet.notes')} />

      {card.repeat?.kind === 'ongoing' ? <span className={styles.hint}>{t.t('card.last', { when: formatRelative(lastTouch(card), tickNow, t.locale) })}</span> : null}

      <div className={controls.group}>
        <div className={controls.label}>{boards.length > 1 ? `${t.t('sheet.board')} · ${t.t('sheet.motivation')}` : t.t('sheet.motivation')}</div>
        <div className={controls.row}>
          {boards.length > 1 ? (
            <select
              className={`${controls.field} ${styles.boardSelect}`}
              value={card.boardId}
              aria-label={t.t('sheet.board')}
              onChange={(e) => {
                const to = boards.find((b) => b.id === e.target.value);
                if (!to || to.id === card.boardId) return;
                const from = card.boardId;
                void moveToBoard(card.id, to.id);
                close();
                showToast(t.t('toast.movedToBoard', { board: to.name }), { label: t.t('toast.undo'), run: () => void moveToBoard(card.id, from) });
              }}
            >
              {boards.map((b) => (
                <option key={b.id} value={b.id}>
                  {b.name}
                </option>
              ))}
            </select>
          ) : null}
          <button type="button" className={controls.square} onClick={() => nudge(-1)} disabled={card.motivation === 0} aria-label={t.t('sheet.less')}>
            <ChevronLeft />
          </button>
          <div className={styles.motion}>
            <span className={styles.dot} style={{ background: `var(--m${card.motivation})` }} />
            <span className={`display ${styles.motionName}`} style={{ color: `var(--m${card.motivation})` }}>
              {t.column(card.motivation)}
            </span>
          </div>
          <button type="button" className={controls.square} onClick={() => nudge(1)} disabled={card.motivation === columnCount - 1} aria-label={t.t('sheet.more')}>
            <ChevronRight />
          </button>
        </div>
      </div>

      <div className={styles.twoUp}>
        <div className={controls.group}>
          <div className={controls.label}>{t.t(bandPreset === 'horizon' ? 'settings.bandPreset.horizon' : 'settings.bandPreset.importance')}</div>
          <div className={controls.chips}>
            {BANDS.map((b) => {
              const active = bandPreset === 'horizon' ? horizonBand(card, now) === b : card.band === b;
              const pick = () => {
                if (active) return;
                if (bandPreset === 'horizon') void updateCard(card.id, { dueAt: dueForHorizon(b, card, now) });
                else void moveCard(card.id, card.motivation, b, 0, { cause: 'sheet' });
              };
              return (
                <button key={b} type="button" className={controls.chip} aria-pressed={active} onClick={pick}>
                  {t.band(b)}
                </button>
              );
            })}
          </div>
        </div>

        <div className={controls.group}>
          <div className={controls.label}>
            {t.t('sheet.energy')} · <span className={styles.levelName}>{t.t(`energy.${card.energy}`)}</span>
          </div>
          <div className={controls.segs} role="radiogroup" aria-label={t.t('sheet.energy')}>
            {LEVELS.map((level) => (
              <button
                key={level}
                type="button"
                role="radio"
                className={`${controls.seg} ${styles.energySeg}`}
                aria-checked={card.energy === level}
                aria-label={t.t(`energy.${level}`)}
                title={t.t(`energy.${level}`)}
                onClick={() => level !== card.energy && void updateCard(card.id, { energy: level })}
              >
                <EnergyGlyph level={level} />
              </button>
            ))}
          </div>
        </div>
      </div>

      <Section id="step" title={t.t('sheet.sectionStep')} summary={stepSummary} open={sectionOpen('step')} onToggle={() => toggleSection('step')}>
      <div className={controls.group}>
        <div className={controls.label}>{t.t('sheet.firstStep')}</div>
        <input ref={firstStepEl} className={controls.field} value={firstStep} placeholder={t.t('sheet.firstStepHint')} onChange={(e) => setFirstStep(e.target.value)} onBlur={commitFirstStep} aria-label={t.t('sheet.firstStep')} maxLength={200} />
        {runningHere ? (
          <div className={controls.row}>
            <span className={styles.timerLeft}>{t.t('timer.left', { time: timeLeft(runningHere, tickNow) })}</span>
            <button type="button" className={`${controls.btn} ${controls.quiet}`} onClick={stopTimer}>
              {t.t('timer.stop')}
            </button>
          </div>
        ) : askFor === card.id ? (
          <div className={styles.feel} role="group" aria-label={t.t('feel.title')}>
            <span className={styles.feelTitle}>{t.t('feel.title')}</span>
            <div className={controls.chips}>
              <button type="button" className={controls.chip} onClick={() => feel(-1)} disabled={card.motivation === 0}>
                {t.t('feel.worse')}
              </button>
              <button type="button" className={controls.chip} onClick={() => feel(0)}>
                {t.t('feel.same')}
              </button>
              <button type="button" className={controls.chip} onClick={() => feel(1)} disabled={card.motivation === columnCount - 1}>
                {t.t('feel.better')}
              </button>
            </div>
          </div>
        ) : (
          <div className={controls.row}>
            <button
              type="button"
              className={`${controls.btn} ${controls.quiet} ${styles.timerBtn}`}
              onClick={() => {
                void startTimer(card.id);
                close();
              }}
            >
              <Timer size={18} />
              {t.t('timer.start')}
            </button>
          </div>
        )}
        {lastAttempt ? (
          <span className={styles.hint}>
            {t.tn('timer.tried', card.attempts.length)} · {t.t('timer.last', { when: formatRelative(lastAttempt.at, tickNow, t.locale) })}
          </span>
        ) : null}
      </div>
      </Section>

      <Section id="plan" title={t.t('sheet.sectionPlan')} summary={planSummary} open={sectionOpen('plan')} onToggle={() => toggleSection('plan')}>
      <div className={controls.group}>
        <div className={controls.label}>{t.t('sheet.due')}</div>
        <div className={controls.row}>
          <input
            type="date"
            className={controls.field}
            value={card.dueAt === null ? '' : toISODate(card.dueAt)}
            onChange={(e) => {
              const day = e.target.value ? fromISODate(e.target.value) : null;
              void updateCard(card.id, { dueAt: day === null ? null : withTime(day, dueTimed && card.dueAt !== null ? toTimeString(card.dueAt) : null) });
            }}
            aria-label={t.t('sheet.due')}
          />
          <input
            type="time"
            className={`${controls.field} ${styles.time}`}
            value={dueTimed && card.dueAt !== null ? toTimeString(card.dueAt) : ''}
            disabled={card.dueAt === null}
            onChange={(e) => card.dueAt !== null && void updateCard(card.id, { dueAt: withTime(card.dueAt, e.target.value || null) })}
            aria-label={t.t('sheet.dueTime')}
            title={t.t('sheet.dueTime')}
          />
          <button type="button" className={controls.square} onClick={() => void updateCard(card.id, { dueAt: null })} disabled={card.dueAt === null} aria-label={t.t('sheet.clearDue')}>
            <Cross size={18} />
          </button>
        </div>
      </div>

      <div className={controls.group}>
        <div className={controls.label}>{t.t('sheet.repeat')}</div>
        <select className={controls.field} value={card.repeat?.kind ?? ''} onChange={(e) => pickRepeatKind(e.target.value)} aria-label={t.t('sheet.repeat')}>
          <option value="">{t.t('repeat.never')}</option>
          {REPEAT_KINDS.map((k) => (
            <option key={k} value={k}>
              {t.t(`repeat.${k}`)}
            </option>
          ))}
        </select>
        {card.repeat?.kind === 'after' ? (
          <div className={controls.row}>
            <input type="number" min={1} max={REPEAT_MAX_DAYS} className={`${controls.field} ${styles.days}`} value={card.repeat.days} onChange={(e) => setRepeat({ ...(card.repeat as Repeat & { kind: 'after' }), days: clampDays(e.target.value, 7) })} aria-label={t.t('repeat.afterDaysAria')} />
            <span className={styles.unit}>{t.t('repeat.afterDays')}</span>
          </div>
        ) : null}
        {card.repeat?.kind === 'weekly' ? (
          <div className={controls.chips} role="group" aria-label={t.t('repeat.weekdays')}>
            {WEEKDAYS.map((d, i) => {
              const r = card.repeat as Repeat & { kind: 'weekly' };
              const on = r.days.includes(d);
              return (
                <button
                  key={d}
                  type="button"
                  className={`${controls.chip} ${styles.weekday}`}
                  aria-pressed={on}
                  onClick={() => {
                    const days = on ? r.days.filter((x) => x !== d) : [...r.days, d].sort((a, b) => a - b);
                    if (days.length) setRepeat({ ...r, days });
                  }}
                >
                  {weekdayNames(t.locale)[i]}
                </button>
              );
            })}
          </div>
        ) : null}
        {card.repeat?.kind === 'monthly' ? (
          <div className={controls.row}>
            <span className={styles.unit}>{t.t('repeat.monthDay')}</span>
            <input type="number" min={1} max={31} className={`${controls.field} ${styles.days}`} value={card.repeat.day} onChange={(e) => setRepeat({ ...(card.repeat as Repeat & { kind: 'monthly' }), day: Math.min(31, clampDays(e.target.value, 1)) })} aria-label={t.t('repeat.monthDay')} />
          </div>
        ) : null}
        {card.repeat?.kind === 'yearly' ? (
          <div className={controls.row}>
            <select className={controls.field} value={card.repeat.month} onChange={(e) => setRepeat({ ...(card.repeat as Repeat & { kind: 'yearly' }), month: Number(e.target.value) })} aria-label={t.t('repeat.month')}>
              {monthNames(t.locale).map((name, i) => (
                <option key={i} value={i + 1}>
                  {name}
                </option>
              ))}
            </select>
            <input type="number" min={1} max={31} className={`${controls.field} ${styles.days}`} value={card.repeat.day} onChange={(e) => setRepeat({ ...(card.repeat as Repeat & { kind: 'yearly' }), day: Math.min(31, clampDays(e.target.value, 1)) })} aria-label={t.t('repeat.monthDay')} />
          </div>
        ) : null}
        {card.repeat?.kind === 'ongoing' ? (
          <div className={controls.row}>
            <span className={styles.unit}>{t.t('repeat.rhythm')}</span>
            <input type="number" min={1} max={REPEAT_MAX_DAYS} className={`${controls.field} ${styles.days}`} value={card.repeat.days} onChange={(e) => setRepeat({ kind: 'ongoing', days: clampDays(e.target.value, 3) })} aria-label={t.t('repeat.rhythmAria')} />
            <span className={styles.unit}>{t.t('repeat.daysUnit')}</span>
          </div>
        ) : null}
        {card.repeat && card.repeat.kind !== 'ongoing' ? (
          <div className={controls.row}>
            <span className={styles.unit}>{t.t('repeat.lead')}</span>
            <select className={controls.field} value={card.repeat.lead} onChange={(e) => setRepeat({ ...(card.repeat as Exclude<Repeat, { kind: 'ongoing' }>), lead: Number(e.target.value) })} aria-label={t.t('repeat.lead')}>
              {LEADS.map((l) => (
                <option key={l} value={l}>
                  {t.t(`repeat.lead.${l}`)}
                </option>
              ))}
            </select>
          </div>
        ) : null}
        {card.repeat && card.doneLog.length ? (
          <span className={styles.hint}>
            {t.tn(card.repeat.kind === 'ongoing' ? 'repeat.touchedCount' : 'repeat.doneCount', card.doneLog.length)} · {t.t('timer.last', { when: formatRelative(card.doneLog[card.doneLog.length - 1] as number, tickNow, t.locale) })}
          </span>
        ) : null}
        {resting && wakeDate !== null ? (
          <div className={controls.row}>
            <span className={styles.hint}>{t.t('repeat.resting', { date: formatShortDate(wakeDate, t.locale) })}</span>
            <button type="button" className={`${controls.btn} ${controls.quiet}`} onClick={() => void wake(card.id)}>
              {t.t('repeat.wake')}
            </button>
          </div>
        ) : null}
        {card.repeat ? <span className={styles.hint}>{t.t(card.repeat.kind === 'ongoing' ? 'repeat.ongoingHint' : 'repeat.hint')}</span> : null}
      </div>

      <div className={controls.group}>
        <div className={controls.label}>{t.t('sheet.reminders')}</div>
        {card.reminders.length ? (
          <ul className={styles.reminderList}>
            {card.reminders.map((r, i) => (
              <li key={i} className={styles.reminderRow}>
                <button
                  type="button"
                  className={`${controls.square} ${styles.ringToggle}`}
                  aria-pressed={r.ring === true}
                  onClick={() => setReminders(card.reminders.map((x, j) => (j === i ? { ...x, ring: !x.ring } : x)))}
                  aria-label={t.t(r.ring ? 'reminder.ringOn' : 'reminder.ringOff')}
                  title={t.t(r.ring ? 'reminder.ringOn' : 'reminder.ringOff')}
                >
                  {r.ring ? <Alarm size={18} /> : <Bell size={18} />}
                </button>
                <span className={styles.reminderText}>{reminderLabel(r)}</span>
                <button type="button" className={controls.square} onClick={() => setReminders(card.reminders.filter((_, j) => j !== i))} aria-label={t.t('reminder.remove')} title={t.t('reminder.remove')}>
                  <Cross size={16} />
                </button>
              </li>
            ))}
          </ul>
        ) : null}
        {card.reminders.length < MAX_REMINDERS ? (
          <select ref={reminderEl} className={controls.field} value="" onChange={(e) => onPickReminder(e.target.value)} aria-label={t.t('sheet.addReminder')}>
            <option value="">{t.t('sheet.addReminder')}</option>
            {REMINDER_PRESETS.map((preset) => (
              <option key={preset.key} value={preset.key} disabled={card.dueAt === null}>
                {t.t(preset.key === 'due' ? 'reminder.atDue' : (`reminder.before.${preset.key}` as 'reminder.before.1h'))}
              </option>
            ))}
            <option value="custom">{t.t('reminder.custom')}</option>
          </select>
        ) : null}
        {customReminder !== null ? (
          <div className={controls.row}>
            <input type="datetime-local" className={controls.field} value={customReminder} onChange={(e) => setCustomReminder(e.target.value)} aria-label={t.t('reminder.custom')} />
            <button type="button" className={controls.square} onClick={() => setCustomReminder(null)} aria-label={t.t('sheet.cancel')} title={t.t('sheet.cancel')}>
              <Cross size={18} />
            </button>
            <button
              type="button"
              className={`${controls.btn} ${controls.primary}`}
              disabled={!customReminder || fromLocalDateTime(customReminder) === null}
              onClick={() => {
                const at = customReminder ? fromLocalDateTime(customReminder) : null;
                if (at === null) return;
                addReminder({ kind: 'at', at });
                setCustomReminder(null);
              }}
            >
              {t.t('reminder.add')}
            </button>
          </div>
        ) : null}
        {card.dueAt === null ? <span className={styles.hint}>{t.t('reminder.needsDue')}</span> : null}
        {card.reminders.length ? <span className={styles.hint}>{t.t('reminder.ringHint')}</span> : null}
        {isNative ? null : <span className={styles.hint}>{t.t('sheet.reminderHint')}</span>}
      </div>
      </Section>

      {card.motivation === 0 ? (
        <Section id="dread" title={t.t('dread.why')} summary={card.dread ? t.t(`dread.${card.dread}`) : null} open={sectionOpen('dread')} onToggle={() => toggleSection('dread')}>
          <>
          <div className={`${controls.chips} ${styles.dreadChips}`} role="group" aria-label={t.t('dread.why')}>
            {DREAD_REASONS.map((r) => (
              <button
                key={r}
                type="button"
                className={`${controls.chip} ${styles.dreadChip}`}
                aria-pressed={card.dread === r}
                onClick={() => {
                  const next: DreadReason | null = card.dread === r ? null : r;
                  void updateCard(card.id, { dread: next });
                  if (next) void logEvent('dread', card.id, { reason: next });
                }}
              >
                {t.t(`dread.${r}`)}
              </button>
            ))}
          </div>
          {card.dread ? (
            <div className={controls.row}>
              <span className={styles.hint}>{t.t(`dread.${card.dread}.move`)}</span>
              <button
                type="button"
                className={`${controls.btn} ${controls.quiet}`}
                onClick={() => {
                  const r = card.dread;
                  if (r === 'unclear' || r === 'big') {
                    openSection('step');
                    requestAnimationFrame(() => firstStepEl.current?.focus());
                  } else if (r === 'waiting') {
                    openSection('plan');
                    requestAnimationFrame(() => reminderEl.current?.focus());
                  } else {
                    void startTimer(card.id);
                    close();
                  }
                }}
              >
                {t.t(`dread.${card.dread}.action`)}
              </button>
            </div>
          ) : null}
          </>
        </Section>
      ) : null}

      {mine.map((c) => (
        <ConflictBlock key={c.id} conflict={c} card={card} onUseTheirs={() => void applyTheirs(card, c, updateCard).then(() => dismissConflict(c.id))} onKeepMine={() => void dismissConflict(c.id)} />
      ))}

      {confirmingDelete ? (
        <div className={`${controls.footer} ${styles.footer}`}>
          <span className={`${styles.confirmText} ${controls.footerLeft}`}>{t.t('sheet.confirmDelete')}</span>
          <button type="button" className={`${controls.btn} ${controls.quiet}`} onClick={() => setConfirmingDelete(false)} autoFocus>
            {t.t('sheet.keep')}
          </button>
          <button type="button" className={`${controls.btn} ${controls.danger}`} onClick={del}>
            {t.t('sheet.delete')}
          </button>
        </div>
      ) : (
        <div className={`${controls.footer} ${styles.footer}`}>
          <button type="button" className={`${controls.btn} ${controls.quiet} ${styles.deleteBtn} ${controls.footerLeft}`} onClick={() => setConfirmingDelete(true)}>
            {t.t('sheet.delete')}
          </button>
          <button type="button" className={`${controls.btn} ${controls.quiet}`} onClick={cancel}>
            {t.t('sheet.cancel')}
          </button>
          <button type="button" className={`${controls.btn} ${controls.primary}`} onClick={close}>
            {t.t('sheet.close')}
          </button>
        </div>
      )}
    </>
  );
}

function applyTheirs(card: Card, conflict: Conflict, updateCard: (id: string, changes: Partial<Card>) => Promise<void>): Promise<void> {
  return updateCard(card.id, { [conflict.field]: conflict.losingValue } as Partial<Card>);
}

function ConflictBlock({ conflict, card, onUseTheirs, onKeepMine }: { conflict: Conflict; card: Card; onUseTheirs(): void; onKeepMine(): void }) {
  const t = useT();
  const shown = (v: unknown): string => (v === null || v === undefined ? '' : Array.isArray(v) ? (conflict.field === 'reminders' ? t.t('card.reminders', { n: v.length }) : String(v.length)) : String(v));
  const value = shown(conflict.losingValue);
  const same = JSON.stringify(card[conflict.field] ?? null) === JSON.stringify(conflict.losingValue ?? null);
  return (
    <div className={styles.conflict}>
      <div>
        <strong>{conflict.deviceName ? t.t('conflict.editedOn', { device: conflict.deviceName }) : t.t('conflict.editedElsewhere')}</strong>{' '}
        {same ? null : t.t('conflict.theirs', { value })}
      </div>
      <div className={controls.footer}>
        <button type="button" className={controls.chip} onClick={onKeepMine}>
          {t.t('conflict.keepMine')}
        </button>
        <button type="button" className={controls.chip} onClick={onUseTheirs} disabled={same}>
          {t.t('conflict.useTheirs')}
        </button>
      </div>
    </div>
  );
}
